/* eslint-disable react/no-unescaped-entities */
import {
  CSSProperties,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { ArrowBackOutlined } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { Button, Text } from "$components";
import useExport from "$hooks/useExport";
import useAuth from "$hooks/useAuth";
import JModal from "$components/JModal/JModal";
import { getEntityNameFromType } from "$utils/typeConstants";
import { ImportError, ImportType } from "$types/import/ImportType";
import { Loader } from "$components/Layout/Loader";

const styles = {
  errorBox: {
    marginTop: 20,
    paddingTop: 10,
    borderTop: "1px solid #ddd",
    width: "100%",
    fontSize: 14,
    maxHeight: "var(--fourtyVh)",
    overflowY: "auto",
  } as CSSProperties,
  errorSpan: {
    color: "tomato",
    fontWeight: "bold",
  } as CSSProperties,
  sumTitleSpan: {
    color: "#030f85",
    fontWeight: "bold",
  } as CSSProperties,
  itemSpan: {
    color: "#030f85",
    fontWeight: "normal",
  } as CSSProperties,
  zone: {
    alignItems: "center",
    border: "2px dashed #CCC",
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    padding: 20,
    cursor: "pointer",
    paddingBottom: 20,
    marginBottom: 20,
  } as CSSProperties,
  dropZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  } as CSSProperties,
  errorMessageArea: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    maxHeight: "70vh",
  } as CSSProperties,
};

type Props = {
  showImportModal: boolean;
  setShowImportModal: (value: boolean) => void;
  type: ImportType;
  exportButton?: ReactNode;
  canExportData?: boolean;
  dataName?: string;
  onImportFinish?: (value?: any) => void;
  openSnackbar: any;
  /**
   * Depends on import type but could be DockyardId, ProjectId, CompanyId, Agency, ... (cf "getDefaultValuesForParsing" bodyContextId)
   * */
  contextId?: string;
  filters?: any;
  isFeminine?: boolean;
  customDownload?: () => void;
  isExportingOrdersList?: boolean;
  validationLabel?: string;
  pastParticiple?: string;
};

function JImportModal({
  showImportModal,
  setShowImportModal,
  type,
  onImportFinish,
  openSnackbar,
  contextId,
  exportButton,
  canExportData,
  dataName,
  filters,
  isFeminine = false,
  customDownload,
  isExportingOrdersList = false,
  validationLabel = "Enregistrer",
  pastParticiple = "importé",
}: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [spinner, setSpinner] = useState(false);
  const [analyzeImportSuccess, setAnalyzeImportSuccess] = useState(false);
  const [analyzeImportErrors, setAnalyzeImportErrors] = useState<ImportError[]>(
    []
  );
  const [showAnalyzeImportResult, setShowAnalyzeImportResult] = useState(false);
  const [fByteArray, setFByteArray] = useState(null);
  const [isExporting, setIsExporting] = useState(false); // prevent the modal to be closed when downloading an exported file

  // Reset import modal on type changed and when showImportModal toggle to false :
  useEffect(() => {
    if (!showImportModal) {
      setSelectedFile(null);
      setSpinner(false);
      setAnalyzeImportSuccess(false);
      setAnalyzeImportErrors([]);
      setShowAnalyzeImportResult(false);
      setFByteArray(null);
      setIsExporting(false);
    }
  }, [type, showImportModal]);

  const {
    errors,
    setErrors,
    importItems,
    analyzeImportItems,
    exportXLS,
    exportXLSTemplate,
    isLoading,
    setIsLoading,
  } = useExport();
  const { token } = useAuth();

  const analyzeDatas = useCallback(
    async (fileByteArray: any) => {
      const body = {
        id: contextId,
        fileByteArray,
      };

      setSpinner(true);
      const datas: any = await analyzeImportItems(token, body, type);
      setSpinner(false);
      setShowAnalyzeImportResult(true);
      setAnalyzeImportErrors(datas.warnings || []);

      if (datas && datas.status === 200 && !datas.errors) {
        setAnalyzeImportSuccess(true);
      } else {
        setAnalyzeImportSuccess(false);
      }
    },
    [contextId, analyzeImportItems, token, type]
  );

  useEffect(() => {
    if (selectedFile) {
      setErrors([]);
      const reader = new FileReader();
      const fileByteArray: any[] = [];
      reader.readAsArrayBuffer(selectedFile);
      setSelectedFile(null);
      reader.onloadend = (evt) => {
        if (evt.target && evt.target.readyState === FileReader.DONE) {
          const arrayBuffer = evt.target.result;
          if (arrayBuffer) {
            const array = new Uint8Array(arrayBuffer as ArrayBufferLike);
            array.forEach((a) => fileByteArray.push(a));
            analyzeDatas(fileByteArray);
            setFByteArray(fileByteArray);
          }
        }
      };
    }
  }, [analyzeDatas, selectedFile, setErrors]);

  const onDrop = useCallback((acceptedFiles: any) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setIsExporting(false);
    }
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
  });

  useEffect(() => {
    if (fileRejections && fileRejections.length > 0) {
      setShowAnalyzeImportResult(true);
      setAnalyzeImportSuccess(false);
    }
  }, [fileRejections]);

  const downloadTemplate = async () => {
    setIsExporting(true);
    try {
      await exportXLSTemplate(token, type, `modèle-${dataName}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportXLS(token, { ...filters, contextId }, type, dataName);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async () => {
    try {
      setIsLoading(true);
      const body = { id: contextId, fileByteArray: fByteArray };
      const importResult: any = await importItems(token, body, type);

      if (importResult.status === 200) {
        setShowImportModal(false);
        setAnalyzeImportSuccess(false);
        setShowAnalyzeImportResult(false);
        setAnalyzeImportErrors([]);
        setErrors([]);
        setSelectedFile(null);
        setFByteArray(null);
        setSpinner(false);
        setIsExporting(false);

        // Close the modal
        setShowImportModal(false);

        const isPlural = importResult.CRUDLength > 1;
        const allVerbs = {
          feminine: {
            singular: `a été ${pastParticiple}`,
            plural: `ont été ${pastParticiple}es`,
          },
          masculine: {
            singular: `a été ${pastParticiple}`,
            plural: `ont été ${pastParticiple}s`,
          },
        };
        const genderedVerb = isFeminine
          ? allVerbs.feminine
          : allVerbs.masculine;
        const verb = isPlural ? genderedVerb.plural : genderedVerb.singular;
        openSnackbar(
          `${importResult.CRUDLength} ${getEntityNameFromType(
            type,
            isPlural
          )} ${verb}.`,
          5000
        );
        if (onImportFinish) {
          await onImportFinish(importResult);
        }
      }
    } catch (err) {
      console.error("Error while importing file", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelImport = () => {
    if (!isExporting && !isLoading) {
      setErrors([]);
      setAnalyzeImportSuccess(false);
      setShowAnalyzeImportResult(false);
      setAnalyzeImportErrors([]);
      setSelectedFile(null);
      setFByteArray(null);
      setSpinner(false);
      setIsExporting(false);
      setShowImportModal(false);
    }
  };

  const handleOnGoBackClick = () => {
    setAnalyzeImportSuccess(false);
    setShowAnalyzeImportResult(false);
    setAnalyzeImportErrors([]);
    setErrors([]);
  };

  const getLoader = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        <Loader text="Veuillez patienter..." />
      </div>
    );
  };

  const renderErrorsAndWarnings = () => {
    if (analyzeImportErrors.length > 0 || errors.length > 0) {
      return (
        <div style={styles.errorMessageArea}>
          {[...analyzeImportErrors, ...errors].map((error) => (
            <div key={`error-${error.line}${error.column}`}>
              <Text
                type={error.severity === "WARN" ? "warning" : "error"}
              >{`Cellule ${error.column}${error.line} : `}</Text>
              <Text>{error.message}</Text>
            </div>
          ))}
        </div>
      );
    }
  };

  const renderImportResults = () => {
    if (analyzeImportErrors.length > 0 || errors.length > 0) {
      return renderErrorsAndWarnings();
    }

    if (!showAnalyzeImportResult) {
      return (
        <div style={styles.zone} {...getRootProps()}>
          <input {...getInputProps()} />
          <Text>Cliquez pour importer un fichier XLS</Text>
        </div>
      );
    }

    if (analyzeImportSuccess) {
      return (
        <>
          <Text>Fichier analysé avec succès</Text>
          {renderErrorsAndWarnings()}
        </>
      );
    }

    if (fileRejections?.length > 0) {
      return (
        <Text>
          Le format du fichier n'est pas le bon. Veuillez importer un fichier
          .xlsx
        </Text>
      );
    }

    return (
      <Text>
        Une erreur inattendue est survenue lors de l'analyse du fichier,
        veuillez réessayer.
      </Text>
    );
  };

  if (!showImportModal) return null;

  return (
    <JModal
      hideConfirm={!showAnalyzeImportResult || !analyzeImportSuccess}
      labelOk={validationLabel}
      labelCancel="Annuler"
      hideClose={
        !showAnalyzeImportResult &&
        errors.length === 0 &&
        analyzeImportErrors.length === 0
      }
      onConfirm={handleImportFile}
      onClose={() => handleCancelImport()}
      isLoading={isLoading}
    >
      <div
        className="buttons"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Button
          onClick={() =>
            showAnalyzeImportResult
              ? handleOnGoBackClick()
              : handleCancelImport()
          }
          variant="text"
          style={{ marginRight: 16 }}
        >
          <ArrowBackOutlined style={{ marginRight: 8 }} />
          Retour
        </Button>
        {/* Temp: disable edb export because of request too heavy */}
        {canExportData && !showAnalyzeImportResult && (
          <Button
            onClick={customDownload || handleExport}
            variant="text"
            disabled={isExporting}
          >
            <Upload2LineIcon />
            {`Exporter les ${dataName}`}
          </Button>
        )}
        {!canExportData &&
          !showAnalyzeImportResult &&
          (exportButton || (
            <Button onClick={customDownload || downloadTemplate} variant="text">
              <Upload2LineIcon />
              Télécharger un modèle
            </Button>
          ))}
      </div>
      {(isExporting || isExportingOrdersList) && getLoader()}
      {spinner ? getLoader() : renderImportResults()}
    </JModal>
  );
}

export default JImportModal;
